import { useEffect, useState } from 'react';
import { Building2, Users, LogIn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import adminDashboardService, { type DashboardStats } from '@/services/adminDashboard';
import empresaService, { type Empresa } from '@/services/empresaAdmin';
import usuarioService, { type Usuario } from '@/services/userAdmin';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { Card, CardHead } from '@/Components/ui/Card';
import { StatusBadge } from '@/Components/ui/Badge';
import { Button } from '@/Components/ui/Button';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    total_usuarios: 0,
    usuarios_ativos: 0,
    acessos_hoje: 0,
    acessos_mes: 0,
  });
  const [loading, setLoading] = useState(false);
  const [empresasRecentes, setEmpresasRecentes] = useState<Empresa[]>([]);
  const [usuariosRecentes, setUsuariosRecentes] = useState<Usuario[]>([]);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [statsData, empresas, usuarios] = await Promise.all([
        adminDashboardService.stats(),
        empresaService.listar(),
        usuarioService.listar(),
      ]);
      setStats(statsData);
      setEmpresasRecentes(empresas.slice(0, 5));
      setUsuariosRecentes(usuarios.slice(0, 5));
    } catch (error) {
      console.error('Erro ao carregar dashboard admin', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="nx-page">
      <PageHeader title="Dashboard Administrativo" description="Visão geral de empresas, usuários e acessos" />

      <div className="nx-kpi-grid">
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Empresas Ativas</span>
            <div className="nx-kpi-icon">
              <Building2 size={16} strokeWidth={2.2} />
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value">
              {stats.empresas_ativas ?? 0}
              <span className="opacity-50"> / {stats.total_empresas ?? 0}</span>
            </strong>
          </div>
        </article>
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Usuários Ativos</span>
            <div className="nx-kpi-icon">
              <Users size={16} strokeWidth={2.2} />
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value">
              {stats.usuarios_ativos}
              <span className="opacity-50"> / {stats.total_usuarios}</span>
            </strong>
          </div>
        </article>
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Acessos Hoje</span>
            <div className="nx-kpi-icon">
              <LogIn size={16} strokeWidth={2.2} />
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value">
              {stats.acessos_hoje}
              <span className="opacity-50"> / {stats.acessos_mes} mês</span>
            </strong>
          </div>
        </article>
      </div>

      <div className="nx-grid-2">
        <Card className="nx-page-card">
          <CardHead
            title="Últimas Empresas"
            actions={
              <Button type="button" variant="ghost" onClick={() => navigate('/admin/empresas')}>
                Ver todas
              </Button>
            }
          />
          {loading ? (
            <div className="nx-table-loading">Carregando…</div>
          ) : empresasRecentes.length === 0 ? (
            <EmptyState message="Nenhuma empresa cadastrada." />
          ) : (
            <ul className="nx-stack nx-stack--md">
              {empresasRecentes.map((empresa) => (
                <li key={empresa.id} className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">{empresa.trade_name || empresa.social_name}</p>
                    <p className="text-xs opacity-70">
                      {empresa.cnpj} · {empresa.city}/{empresa.state}
                    </p>
                  </div>
                  <StatusBadge label={empresa.is_active ? 'Ativo' : 'Inativo'} tone={empresa.is_active ? 'success' : 'danger'} />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="nx-page-card">
          <CardHead
            title="Últimos Usuários"
            actions={
              <Button type="button" variant="ghost" onClick={() => navigate('/admin/usuarios')}>
                Ver todos
              </Button>
            }
          />
          {loading ? (
            <div className="nx-table-loading">Carregando…</div>
          ) : usuariosRecentes.length === 0 ? (
            <EmptyState message="Nenhum usuário cadastrado." />
          ) : (
            <ul className="nx-stack nx-stack--md">
              {usuariosRecentes.map((usuario) => (
                <li key={usuario.id} className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">
                      {usuario.first_name || usuario.username} {usuario.last_name || ''}
                    </p>
                    <p className="text-xs opacity-70">{usuario.email}</p>
                  </div>
                  <StatusBadge label={usuario.is_active ? 'Ativo' : 'Inativo'} tone={usuario.is_active ? 'success' : 'danger'} />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}
